import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { canAccessFatturazioneElettronica } from "../utils/fatturazioneAccess";
import {
  Bed,
  Users,
  Home as HomeIcon,
  TrendingUp,
  Receipt,
  FileText,
  Package,
  ShoppingCart,
  Upload,
  Bell,
  BarChart3,
  UserPlus,
  AlertTriangle,
  ShieldCheck,
  BookOpen,
  GripVertical,
  Eye,
  EyeOff,
} from "lucide-react";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DashboardAggregatePanel from "./DashboardAggregatePanel";
import ControlloCardItem from "./dashboard/ControlloCardItem";
import {
  CONTROLLO_CARDS_BY_ID,
  CONTROLLO_SIZE_LABELS,
  getControlloColSpanClass,
} from "../config/controlloCards";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_DIARIO_STATS_FALLBACK = {
  consegne_oggi: 0,
  segnalazioni_oggi: 0,
  data: "",
  per_turno: {
    mattina: { consegne: 0, segnalazioni: 0 },
    pomeriggio: { consegne: 0, segnalazioni: 0 },
    notte: { consegne: 0, segnalazioni: 0 },
  },
};

/**
 * Safely run a GET request and return `fallback` if it fails (permessi, rete, ecc).
 */
async function safeGet(url, config, fallback = null) {
  try {
    const res = await axios.get(url, config);
    return res.data;
  } catch (err) {
    return fallback;
  }
}

/** Avatar con iniziali da nome/cognome */
const AVATAR_COLORS = [
  "bg-rose-100 text-rose-700",
  "bg-sky-100 text-sky-700",
  "bg-amber-100 text-amber-700",
  "bg-emerald-100 text-emerald-700",
  "bg-violet-100 text-violet-700",
  "bg-cyan-100 text-cyan-700",
  "bg-orange-100 text-orange-700",
];
function getIniziali(nome = "", cognome = "") {
  const a = (nome || "").trim()[0] || "";
  const b = (cognome || "").trim()[0] || "";
  return (a + b).toUpperCase() || "?";
}
function hashColor(str = "") {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function parseData(value) {
  if (!value) return null;
  // Accetta ISO (YYYY-MM-DD) e DD/MM/YYYY
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
    const d = new Date(value);
    return isNaN(d) ? null : d;
  }
  if (/^\d{2}\/\d{2}\/\d{4}/.test(value)) {
    const [dd, mm, yyyy] = value.split("/");
    const d = new Date(`${yyyy}-${mm}-${dd}`);
    return isNaN(d) ? null : d;
  }
  const d = new Date(value);
  return isNaN(d) ? null : d;
}

const MESI_BREVI = ["GEN", "FEB", "MAR", "APR", "MAG", "GIU", "LUG", "AGO", "SET", "OTT", "NOV", "DIC"];

function resolveControlloCardId(cardId, fatturazioneAccess) {
  if (cardId === "kpi-ricavi-mese" && !fatturazioneAccess) return null;
  if (cardId === "kpi-spese-mese" && !fatturazioneAccess) return null;
  if (cardId === "kpi-margine-operativo" && !fatturazioneAccess) return null;
  if (cardId === "kpi-fatture-non-pagate" && !fatturazioneAccess) return null;
  if (cardId === "kpi-piani-scadenza" && fatturazioneAccess) return null;
  if (cardId === "kpi-alloggiati-web" && fatturazioneAccess) return null;
  return cardId;
}

function SortableControlloCard({
  card,
  cardData,
  isCustomizeMode,
  colSpanClass,
  onToggleVisible,
  onChangeSize,
}) {
  const meta = CONTROLLO_CARDS_BY_ID[card.id];
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: card.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: "relative",
  };
  const supportedSizes = meta?.supportedSizes ?? ["kpi"];
  const isCompactKpi = card.size === "kpi";

  if (!isCustomizeMode) {
    if (!card.visible) return null;
    const alignClass = isCompactKpi ? "self-start" : "";
    const innerWrap = isCompactKpi ? "w-full" : "h-full min-h-0 flex flex-col flex-1";
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`min-h-0 flex flex-col ${colSpanClass} ${alignClass}`}
      >
        <div className={innerWrap}>
          <ControlloCardItem cardId={card.id} size={card.size} cardData={cardData} />
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group min-h-0 flex flex-col ${colSpanClass}${isCompactKpi ? " self-start w-full lg:max-w-none" : ""}`}
    >
      {!card.visible && (
        <div className="absolute inset-0 bg-white/70 backdrop-blur-sm rounded-xl z-10 pointer-events-none" />
      )}
      <div className="pointer-events-none select-none min-h-[100px] flex-1 min-h-0">
        <ControlloCardItem cardId={card.id} size={card.size} cardData={cardData} />
      </div>
      <div className="absolute inset-x-0 bottom-0 z-20 flex flex-wrap items-center justify-between gap-1 px-2 py-1.5 bg-black/60 backdrop-blur rounded-b-xl">
        <button
          type="button"
          className="touch-none text-white/70 hover:text-white cursor-grab active:cursor-grabbing p-1 rounded"
          {...attributes}
          {...listeners}
          title="Trascina per riordinare"
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <span className="text-white/80 text-[10px] font-medium truncate flex-1 text-center">
          {meta?.label || card.id}
        </span>
        <select
          value={card.size}
          onChange={(e) => onChangeSize(card.id, e.target.value)}
          className="max-w-[132px] text-[10px] rounded px-1 py-0.5 bg-white/95 text-slate-800 border border-white/40"
          onPointerDown={(e) => e.stopPropagation()}
        >
          {supportedSizes.map((s) => (
            <option key={s} value={s}>
              {CONTROLLO_SIZE_LABELS[s] || s}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => onToggleVisible(card.id)}
          title={card.visible ? "Nascondi" : "Mostra"}
          className={`p-1 rounded transition-all ${
            card.visible ? "text-emerald-300 hover:text-white" : "text-white/40 hover:text-white/70"
          }`}
        >
          {card.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
}

/**
 * Dashboard avanzata - vista "Controllo completo" stile SaaS 2026.
 * Popolata con dati reali dagli endpoint esistenti (fallback graceful se
 * l'utente non ha permessi o se l'endpoint non risponde).
 */
export default function DashboardAdvanced({
  data,
  dashboardFatturazione,
  aggregateControlloView = false,
  hasMultiStruttura = false,
  prefControllo = [],
  isCustomizeMode = false,
  setDraftPrefs,
}) {
  const { user, token, activeStruttura } = useAuth();
  const fatturazioneAccess = dashboardFatturazione ?? canAccessFatturazioneElettronica(user);
  // Chiave struttura attiva: cambia quando l'utente switcha struttura in un contesto multi-struttura
  const strutturaKey = activeStruttura?.id ?? user?.struttura_id ?? "";

  const [loading, setLoading] = useState(true);
  const [postiLiberiInfo, setPostiLiberiInfo] = useState(null);
  const [fascicoli, setFascicoli] = useState([]);
  const [alertPT, setAlertPT] = useState([]);
  const [alertReinvio, setAlertReinvio] = useState({ count_in_scadenza: 0, count_scaduti: 0, has_alert: false });
  const [fatture, setFatture] = useState([]);
  const [metricsCorrente, setMetricsCorrente] = useState(null);
  const [deblisteringInfo, setDeblisteringInfo] = useState(null);
  const [diarioOggi, setDiarioOggi] = useState({
    consegne: 0,
    segnalazioni: 0,
    data: "",
    perTurno: { ...EMPTY_DIARIO_STATS_FALLBACK.per_turno },
  });
  const [preIngressoData, setPreIngressoData] = useState({ items: [], avvisi: [] });

  useEffect(() => {
    if (aggregateControlloView && hasMultiStruttura) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const cfg = { headers };

    (async () => {
      setLoading(true);
      const [
        postiRes,
        fascicoliRes,
        alertPTRes,
        alertReinvioRes,
        fattureRes,
        metricsCorrenteRes,
        deblisteringRes,
        diarioStatsRes,
        preIngressoRes,
      ] = await Promise.all([
        safeGet(`${API_URL}/api/posti-liberi`, cfg, null),
        safeGet(`${API_URL}/api/fascicoli-assistenziali`, cfg, []),
        safeGet(`${API_URL}/api/fascicoli-assistenziali/alert-piani-terapeutici`, cfg, { items: [] }),
        safeGet(`${API_URL}/api/alloggiati-web/alert-reinvio`, cfg, { has_alert: false, count_in_scadenza: 0, count_scaduti: 0 }),
        fatturazioneAccess
          ? safeGet(`${API_URL}/api/fatturazione/elenco-fatture?limit=500`, cfg, { fatture: [] })
          : Promise.resolve({ fatture: [] }),
        fatturazioneAccess
          ? safeGet(`${API_URL}/api/dashboard/metrics-corrente`, cfg, null)
          : Promise.resolve(null),
        safeGet(`${API_URL}/api/deblistering/riepilogo`, cfg, null),
        safeGet(`${API_URL}/api/diario-consegne/stats-oggi`, cfg, EMPTY_DIARIO_STATS_FALLBACK),
        safeGet(`${API_URL}/api/dashboard/pre-ingressi`, cfg, { items: [], avvisi: [] }),
      ]);
      if (cancelled) return;
      setPostiLiberiInfo(postiRes);
      setFascicoli(Array.isArray(fascicoliRes) ? fascicoliRes : []);
      setAlertPT(Array.isArray(alertPTRes?.items) ? alertPTRes.items : []);
      setAlertReinvio(alertReinvioRes || {});
      setFatture(Array.isArray(fattureRes?.fatture) ? fattureRes.fatture : []);
      setMetricsCorrente(metricsCorrenteRes || null);
      setDeblisteringInfo(deblisteringRes);
      const ptRaw = diarioStatsRes?.per_turno || {};
      const perTurno = {
        mattina: {
          consegne: Number(ptRaw.mattina?.consegne) || 0,
          segnalazioni: Number(ptRaw.mattina?.segnalazioni) || 0,
        },
        pomeriggio: {
          consegne: Number(ptRaw.pomeriggio?.consegne) || 0,
          segnalazioni: Number(ptRaw.pomeriggio?.segnalazioni) || 0,
        },
        notte: {
          consegne: Number(ptRaw.notte?.consegne) || 0,
          segnalazioni: Number(ptRaw.notte?.segnalazioni) || 0,
        },
      };
      setDiarioOggi({
        consegne: Number(diarioStatsRes?.consegne_oggi) || 0,
        segnalazioni: Number(diarioStatsRes?.segnalazioni_oggi) || 0,
        data: diarioStatsRes?.data || "",
        perTurno,
      });
      setPreIngressoData({
        items: Array.isArray(preIngressoRes?.items) ? preIngressoRes.items : [],
        avvisi: Array.isArray(preIngressoRes?.avvisi) ? preIngressoRes.avvisi : [],
      });
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
    // Ricarica quando cambia la struttura attiva (switch multi-struttura)
  }, [token, fatturazioneAccess, strutturaKey, aggregateControlloView, hasMultiStruttura]);

  // ------------------------------------------------------------------
  // KPI - Riga superiore
  // ------------------------------------------------------------------
  const fascicoliAttivi = useMemo(
    () => fascicoli.filter((f) => !f.archiviata),
    [fascicoli]
  );
  const fascicoliInStruttura = useMemo(
    () => fascicoliAttivi.filter((f) => f.stato_ospite !== "PRE_INGRESSO"),
    [fascicoliAttivi]
  );
  const ospitiResidenti = useMemo(
    () => fascicoliAttivi.filter((f) => f.residente_in_struttura === true).length,
    [fascicoliAttivi]
  );
  const postiTotali = postiLiberiInfo?.totale_posti_letto ?? data?.kpi?.posti_totali ?? 0;
  const postiLiberi = postiLiberiInfo?.posti_disponibili ?? data?.kpi?.posti_liberi ?? 0;
  const postiOccupati = Math.max(postiTotali - postiLiberi, 0);
  // Escludi pre-ingressi dal conteggio ospiti presenti in struttura
  const ospitiPresenti =
    fascicoliInStruttura.length > 0 ? fascicoliInStruttura.length : postiOccupati;

  const preIngressiWidget = useMemo(() => {
    return (preIngressoData.items || []).map((item) => ({
      id: item.fascicolo_id,
      nome: item.nome_completo,
      ingresso: item.data_ingresso_prevista_label || "—",
      documentazione: `${item.documentazione_percentuale ?? 0}%`,
      stato: item.stato_workflow || "—",
      iniziali: getIniziali(item.nome, item.cognome),
      colore: hashColor(item.nome_completo || ""),
    }));
  }, [preIngressoData.items]);

  const preIngressoAvvisi = useMemo(
    () => preIngressoData.avvisi || [],
    [preIngressoData.avvisi],
  );

  // Fatture: filtro su fatture non pagate (quelle con stato non bozza/rifiutata/scartata e stato_pagamento non pagata)
  const fattureNonPagateList = useMemo(() => {
    return (fatture || []).filter((f) => {
      const residuo = Number(f.totale || 0) - Number(f.importo_pagato || 0);
      const statoPag = (f.stato_pagamento || "").toLowerCase();
      const statoEmessa = !["bozza", "rifiutata", "scartata"].includes((f.stato || "").toLowerCase());
      return statoEmessa && residuo > 0 && statoPag !== "pagata";
    });
  }, [fatture]);
  const fattureNonPagate = fattureNonPagateList.length;
  const totaleNonPagato = fattureNonPagateList.reduce(
    (acc, f) => acc + (Number(f.totale || 0) - Number(f.importo_pagato || 0)),
    0
  );
  const ultimaFatturaNonPagata = fattureNonPagateList[0];

  // Ricavi mese corrente: somma rette previste degli ospiti attivi (dal backend)
  const ricaviMese = metricsCorrente?.ricavi_mese ?? 0;
  const speseMese = metricsCorrente?.spese_mese ?? 0;
  const margineOperativo = metricsCorrente?.margine ?? 0;
  const ospitiConteggio = metricsCorrente?.ospiti_conteggio ?? 0;

  // Variazione ricavi non più disponibile (metrica basata su rette, non su storico fatture)
  const ricaviMesePrecedente = 0;
  const ricaviDeltaPerc = null;

  // Conteggio ingressi/uscite nel mese corrente (per "questo mese")
  const { ingressiMese, usciteMese } = useMemo(() => {
    const oggi = new Date();
    const meseCorrente = oggi.getMonth();
    const annoCorrente = oggi.getFullYear();
    let ing = 0;
    let usc = 0;
    (fascicoli || []).forEach((f) => {
      const di = parseData(f.data_ingresso);
      if (di && di.getMonth() === meseCorrente && di.getFullYear() === annoCorrente) ing += 1;
      const du = parseData(f.data_uscita);
      if (du && du.getMonth() === meseCorrente && du.getFullYear() === annoCorrente) usc += 1;
    });
    return { ingressiMese: ing, usciteMese: usc };
  }, [fascicoli]);

  // Variazione netta ospiti presenti questo mese
  const deltaOspitiMese = ingressiMese - usciteMese;
  // Variazione posti liberi = inverso di delta ospiti (più ospiti = meno posti liberi)
  const deltaPostiLiberiMese = -deltaOspitiMese;

  const formatDelta = (n, suffix = "") =>
    `${n > 0 ? "+" : ""}${n}${suffix}`;

  const occupatePerc = postiTotali > 0 ? Math.round((postiOccupati / postiTotali) * 100) : 0;

  // KPI cards (nasconde/adatta i KPI fatturazione se l'utente non ha accesso)
  const kpiCards = useMemo(() => {
    const cards = [
      {
        key: "posti-totali",
        label: "Posti letto totali",
        value: postiTotali,
        hint: "+0 questo mese",
        hintTone: "neutral",
        icon: Bed,
        iconBg: "bg-emerald-100",
        iconColor: "text-emerald-600",
      },
      {
        key: "ospiti-presenti",
        label: "Ospiti presenti",
        value: ospitiPresenti,
        hint: `${formatDelta(deltaOspitiMese)} questo mese`,
        hintTone: deltaOspitiMese > 0 ? "positive" : deltaOspitiMese < 0 ? "negative" : "neutral",
        icon: Users,
        iconBg: "bg-sky-100",
        iconColor: "text-sky-600",
      },
      {
        key: "ospiti-residenti",
        label: "Residenti in struttura",
        value: ospitiResidenti,
        hint:
          fascicoliAttivi.length > 0
            ? `${Math.round((ospitiResidenti / fascicoliAttivi.length) * 100)}% degli ospiti`
            : "Nessun ospite attivo",
        hintTone: "neutral",
        icon: HomeIcon,
        iconBg: "bg-emerald-100",
        iconColor: "text-emerald-600",
      },
      {
        key: "posti-liberi",
        label: "Camere e posti letto",
        value: postiLiberi,
        hint: `${formatDelta(deltaPostiLiberiMese)} questo mese`,
        hintTone: deltaPostiLiberiMese > 0 ? "positive" : deltaPostiLiberiMese < 0 ? "negative" : "neutral",
        icon: HomeIcon,
        iconBg: "bg-orange-100",
        iconColor: "text-orange-600",
      },
    ];
    if (fatturazioneAccess) {
      const fmtEuro = (n) => n.toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      cards.push(
        {
          key: "ricavi-mese",
          label: "Ricavi mese",
          value: `€ ${fmtEuro(ricaviMese)}`,
          hint: ospitiConteggio > 0 ? `Rette di ${ospitiConteggio} ospiti attivi` : "Nessun ospite attivo",
          hintTone: "neutral",
          icon: TrendingUp,
          iconBg: "bg-purple-100",
          iconColor: "text-purple-600",
        },
        {
          key: "spese-mese",
          label: "Spese mese",
          value: `€ ${fmtEuro(speseMese)}`,
          hint: "Prima nota + fatture passive",
          hintTone: "neutral",
          icon: ShoppingCart,
          iconBg: "bg-rose-100",
          iconColor: "text-rose-600",
        },
        {
          key: "margine-operativo",
          label: "Margine operativo",
          value: `€ ${fmtEuro(margineOperativo)}`,
          hint: ricaviMese > 0
            ? `${Math.round((margineOperativo / ricaviMese) * 100)}% sui ricavi`
            : "—",
          hintTone: margineOperativo >= 0 ? "positive" : "negative",
          icon: Receipt,
          iconBg: "bg-emerald-100",
          iconColor: "text-emerald-600",
        },
        {
          key: "fatture-non-pagate",
          label: "Fatture non pagate",
          value: fattureNonPagate,
          hint: `€ ${totaleNonPagato.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          hintTone: "neutral",
          icon: Receipt,
          iconBg: "bg-amber-100",
          iconColor: "text-amber-600",
        }
      );
    } else {
      // Fallback per utenti senza fatturazione: mostra metriche comunque utili
      cards.push(
        {
          key: "fascicoli-alert",
          label: "Piani terap. scadenza",
          value: alertPT.length,
          hint: alertPT.length === 0 ? "nessuna scadenza" : "entro 30 giorni",
          hintTone: "neutral",
          icon: AlertTriangle,
          iconBg: "bg-amber-100",
          iconColor: "text-amber-600",
        },
        {
          key: "alloggiati-reinvio",
          label: "Alloggiati Web",
          value: alertReinvio?.count_scaduti + alertReinvio?.count_in_scadenza || 0,
          hint: (alertReinvio?.count_scaduti + alertReinvio?.count_in_scadenza || 0) === 0
            ? "tutto in regola"
            : `${alertReinvio.count_scaduti} scaduti · ${alertReinvio.count_in_scadenza} in scadenza`,
          hintTone: "neutral",
          icon: ShieldCheck,
          iconBg: "bg-cyan-100",
          iconColor: "text-cyan-600",
        }
      );
    }
    return cards;
  }, [
    postiTotali,
    postiLiberi,
    ospitiPresenti,
    ospitiResidenti,
    fascicoliAttivi.length,
    deltaOspitiMese,
    deltaPostiLiberiMese,
    ricaviMese,
    speseMese,
    margineOperativo,
    ospitiConteggio,
    fattureNonPagate,
    totaleNonPagato,
    fatturazioneAccess,
    alertPT.length,
    alertReinvio,
  ]);

  // ------------------------------------------------------------------
  // Attività recenti (dati reali)
  // ------------------------------------------------------------------
  const attivitaRecenti = useMemo(() => {
    const items = [];
    if (fatturazioneAccess && fattureNonPagate > 0) {
      items.push({
        icon: Receipt,
        iconBg: "bg-red-50",
        iconColor: "text-red-500",
        titolo: `${fattureNonPagate} ${fattureNonPagate === 1 ? "fattura" : "fatture"} non ${fattureNonPagate === 1 ? "pagata" : "pagate"}`,
        sottotitolo: ultimaFatturaNonPagata
          ? `Ultima: ${ultimaFatturaNonPagata.numero || ultimaFatturaNonPagata.id || "—"}`
          : "",
        valore: `€ ${totaleNonPagato.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        valoreColor: "text-red-500",
        to: "/fatturazione",
      });
    }
    if (alertPT.length > 0) {
      const primo = alertPT[0];
      const dataF = primo?.scadenza
        ? new Date(primo.scadenza).toLocaleDateString("it-IT")
        : "";
      items.push({
        icon: FileText,
        iconBg: "bg-orange-50",
        iconColor: "text-orange-500",
        titolo: `${alertPT.length} ${alertPT.length === 1 ? "documento" : "documenti"} in scadenza`,
        sottotitolo: primo ? `${primo.file_name || "Piano terap."}${dataF ? ` · Scade il ${dataF}` : ""}` : "",
        valore: null,
        to: "/fascicoli-assistenziali",
      });
    }
    if (deblisteringInfo) {
      const daAssegnare = deblisteringInfo.fatture_da_assegnare || 0;
      const daInserire = deblisteringInfo.fatture_da_inserire || 0;
      const totOpenDeb = daAssegnare + daInserire;
      if (totOpenDeb > 0) {
        items.push({
          icon: Package,
          iconBg: "bg-sky-50",
          iconColor: "text-sky-500",
          titolo: "Deblistering da aggiornare",
          sottotitolo:
            daAssegnare > 0 && daInserire > 0
              ? `${daAssegnare} da assegnare · ${daInserire} da fatturare`
              : daAssegnare > 0
              ? `${daAssegnare} fatture da assegnare`
              : `${daInserire} fatture da inserire`,
          valore: null,
          to: "/deblistering",
        });
      }
    }
    if (alertReinvio?.has_alert) {
      const tot = (alertReinvio.count_scaduti || 0) + (alertReinvio.count_in_scadenza || 0);
      items.push({
        icon: ShieldCheck,
        iconBg: "bg-amber-50",
        iconColor: "text-amber-600",
        titolo: `${tot} ${tot === 1 ? "ospite" : "ospiti"} Alloggiati Web`,
        sottotitolo:
          alertReinvio.count_scaduti > 0
            ? `${alertReinvio.count_scaduti} da reinviare ora`
            : `${alertReinvio.count_in_scadenza} in scadenza (48h)`,
        valore: null,
        to: "/alloggiati-web",
      });
    }
    return items.slice(0, 4);
  }, [
    fatturazioneAccess,
    fattureNonPagate,
    totaleNonPagato,
    ultimaFatturaNonPagata,
    alertPT,
    deblisteringInfo,
    alertReinvio,
  ]);

  // ------------------------------------------------------------------
  // Occupazione camere (donut)
  // ------------------------------------------------------------------
  const occupazioneData = useMemo(
    () => [
      { name: "Occupate", value: postiOccupati, color: "#10B981" },
      { name: "Libere", value: postiLiberi, color: "#94A3B8" },
      { name: "Manutenzione", value: 0, color: "#F59E0B" },
      { name: "Non disponibili", value: 0, color: "#EF4444" },
    ],
    [postiOccupati, postiLiberi]
  );
  const occupazioneChartData = useMemo(
    () =>
      occupazioneData.some((d) => d.value > 0)
        ? occupazioneData
        : [{ name: "Nessun dato", value: 1, color: "#E2E8F0" }],
    [occupazioneData],
  );

  // ------------------------------------------------------------------
  // Deblistering (sintesi)
  // ------------------------------------------------------------------
  const debTotale = postiTotali || (deblisteringInfo?.ospiti_attivi ? deblisteringInfo.ospiti_attivi : 0);
  const debAttivi = deblisteringInfo?.ospiti_attivi || 0;
  const debPerc = debTotale > 0 ? Math.round((debAttivi / debTotale) * 100) : 0;
  const debRicavoMensile = deblisteringInfo?.ricavo_mensile_stimato || 0;
  const debPrezzo = deblisteringInfo?.prezzo_blisteraggio || 0;

  // ------------------------------------------------------------------
  // Ultimi ospiti inseriti (dai fascicoli ordinati per data ingresso DESC)
  // ------------------------------------------------------------------
  const ultimiOspiti = useMemo(() => {
    const withDate = fascicoliAttivi
      .map((f) => ({
        ...f,
        _d: parseData(f.data_ingresso),
      }))
      .filter((f) => f._d)
      .sort((a, b) => b._d - a._d);
    const list = withDate.length > 0 ? withDate : fascicoliAttivi;
    return list.slice(0, 4).map((f) => {
      const nomeCompleto = (f.nome_completo || `${f.nome || ""} ${f.cognome || ""}`).trim() || "Ospite";
      const camera = f.camera && f.camera !== "N/A" ? `Camera ${f.camera}` : "Camera —";
      const dataIng = parseData(f.data_ingresso);
      const ingressoStr = dataIng ? dataIng.toLocaleDateString("it-IT") : "—";
      return {
        id: f.id,
        nome: nomeCompleto,
        info: `${camera} · ingresso ${ingressoStr}`,
        iniziali: getIniziali(f.nome, f.cognome),
        colore: hashColor(nomeCompleto),
      };
    });
  }, [fascicoliAttivi]);

  // ------------------------------------------------------------------
  // Scadenze prossime (mix PT + alloggiati web + eventuali fatture in scadenza)
  // ------------------------------------------------------------------
  const scadenzeProssime = useMemo(() => {
    const items = [];
    // Piani terapeutici (già filtrati entro 30 giorni)
    for (const pt of alertPT) {
      const d = parseData(pt.scadenza);
      if (!d) continue;
      const giorni = pt.giorni_rimanenti ?? Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
      const tag =
        giorni < 0
          ? { label: "Scaduto", class: "bg-red-100 text-red-700 ring-red-200", box: "bg-red-50 text-red-700" }
          : giorni <= 7
          ? { label: "Importante", class: "bg-red-100 text-red-700 ring-red-200", box: "bg-red-50 text-red-700" }
          : giorni <= 15
          ? { label: "Attenzione", class: "bg-amber-100 text-amber-800 ring-amber-200", box: "bg-amber-50 text-amber-800" }
          : { label: "Promemoria", class: "bg-sky-100 text-sky-700 ring-sky-200", box: "bg-sky-50 text-sky-700" };
      const giorniLabel =
        giorni < 0
          ? `Scaduto da ${Math.abs(giorni)} ${Math.abs(giorni) === 1 ? "giorno" : "giorni"}`
          : giorni === 0
          ? "Scade oggi"
          : `${giorni} ${giorni === 1 ? "giorno" : "giorni"}`;
      items.push({
        giorno: String(d.getDate()).padStart(2, "0"),
        mese: MESI_BREVI[d.getMonth()],
        titolo: pt.tipo === "cambio_medico"
          ? (pt.file_name || "Cambio o rinnovo medico")
          : pt.tipo === "documento_identita"
          ? (pt.file_name || "Documento identità")
          : pt.tipo === "contratto_cartaceo"
          ? (pt.file_name || "Attestazione contratto cartaceo")
          : (pt.file_name || "Piano terapeutico"),
        sottotitolo: pt.nome_ospite
          ? `${pt.nome_ospite} · ${giorniLabel}`
          : giorniLabel,
        tag: tag.label,
        tagClass: tag.class,
        boxClass: tag.box,
        sortKey: d.getTime(),
        to: pt.fascicolo_id
          ? `/fascicoli-assistenziali/${pt.fascicolo_id}${pt.tipo === "cambio_medico" ? "?tab=sanitaria" : ""}`
          : "/fascicoli-assistenziali",
      });
    }
    // Alloggiati Web
    if (alertReinvio?.has_alert) {
      const tot = (alertReinvio.count_scaduti || 0) + (alertReinvio.count_in_scadenza || 0);
      const d = new Date();
      d.setDate(d.getDate() + (alertReinvio.count_scaduti > 0 ? 0 : 2));
      items.push({
        giorno: String(d.getDate()).padStart(2, "0"),
        mese: MESI_BREVI[d.getMonth()],
        titolo: "Alloggiati Web",
        sottotitolo: `${tot} ${tot === 1 ? "ospite" : "ospiti"} da reinviare`,
        tag: alertReinvio.count_scaduti > 0 ? "Urgente" : "Attenzione",
        tagClass:
          alertReinvio.count_scaduti > 0
            ? "bg-red-100 text-red-700 ring-red-200"
            : "bg-amber-100 text-amber-800 ring-amber-200",
        boxClass:
          alertReinvio.count_scaduti > 0
            ? "bg-red-50 text-red-700"
            : "bg-amber-50 text-amber-800",
        sortKey: d.getTime(),
      });
    }
    // Fatture in scadenza (data_scadenza prossima, non pagate)
    if (fatturazioneAccess) {
      const now = new Date();
      const in30 = new Date();
      in30.setDate(now.getDate() + 30);
      for (const f of fattureNonPagateList) {
        const d = parseData(f.data_scadenza);
        if (!d || d < now || d > in30) continue;
        items.push({
          giorno: String(d.getDate()).padStart(2, "0"),
          mese: MESI_BREVI[d.getMonth()],
          titolo: `Fattura ${f.numero || ""}`.trim() || "Fattura in scadenza",
          sottotitolo: `€ ${(Number(f.totale || 0) - Number(f.importo_pagato || 0)).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`,
          tag: "Promemoria",
          tagClass: "bg-sky-100 text-sky-700 ring-sky-200",
          boxClass: "bg-sky-50 text-sky-700",
          sortKey: d.getTime(),
        });
      }
    }
    items.sort((a, b) => a.sortKey - b.sortKey);
    return items.slice(0, 4);
  }, [alertPT, alertReinvio, fatturazioneAccess, fattureNonPagateList]);

  // ------------------------------------------------------------------
  // Azioni rapide
  // ------------------------------------------------------------------
  const azioniRapide = useMemo(
    () => [
      { label: "Nuovo ospite", icon: UserPlus, color: "text-emerald-600", bg: "bg-emerald-50", ring: "ring-emerald-100", to: "/fascicoli-assistenziali" },
      { label: "Registra spesa", icon: ShoppingCart, color: "text-orange-600", bg: "bg-orange-50", ring: "ring-orange-100", to: "/prima-nota-ospiti" },
      { label: "Nuova fattura", icon: Receipt, color: "text-purple-600", bg: "bg-purple-50", ring: "ring-purple-100", to: fatturazioneAccess ? "/fatturazione/nuova" : "/prima-nota-ospiti" },
      { label: "Carica documento", icon: Upload, color: "text-sky-600", bg: "bg-sky-50", ring: "ring-sky-100", to: "/fascicoli-assistenziali" },
      { label: "Diario consegne", icon: BookOpen, color: "text-blue-600", bg: "bg-blue-50", ring: "ring-blue-100", to: "/diario-consegne" },
      { label: "Documenti", icon: FileText, color: "text-amber-600", bg: "bg-amber-50", ring: "ring-amber-100", to: "/documenti" },
      { label: "Risparmio", icon: BarChart3, color: "text-teal-600", bg: "bg-teal-50", ring: "ring-teal-100", to: "/risparmio" },
    ],
    [fatturazioneAccess],
  );

  const controlloSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const controlloCardData = useMemo(
    () => ({
      kpiCards,
      kpiMetrics: {
        postiTotali,
        postiLiberi,
        postiOccupati,
        ospitiPresenti,
        ospitiResidenti,
        fascicoliAttiviCount: fascicoliInStruttura.length,
        ricaviMese,
        ricaviMesePrecedente,
        ricaviDeltaPerc,
        speseMese,
        margineOperativo,
        ospitiConteggio,
        fattureTotCount: fatture?.length ?? 0,
        fattureNonPagateCount: fattureNonPagate,
        totaleNonPagato,
        alertPTCount: alertPT.length,
        alloggiatiAlertTot:
          (alertReinvio?.count_scaduti ?? 0) + (alertReinvio?.count_in_scadenza ?? 0),
        alertReinvioScaduti: alertReinvio?.count_scaduti ?? 0,
        alertReinvioInScadenza: alertReinvio?.count_in_scadenza ?? 0,
        occupatePerc,
        deltaOspitiMese,
        deltaPostiLiberiMese,
      },
      postiLiberiInfo,
      diarioOggi,
      occupazioneData,
      occupazioneChartData,
      occupatePerc,
      deblisteringInfo,
      debTotale,
      debAttivi,
      debPerc,
      debRicavoMensile,
      debPrezzo,
      attivitaRecenti,
      ultimiOspiti,
      scadenzeProssime,
      preIngressiWidget,
      preIngressoAvvisi,
      azioniRapide,
    }),
    [
      kpiCards,
      postiTotali,
      postiLiberi,
      postiOccupati,
      ospitiPresenti,
      ospitiResidenti,
      fascicoliInStruttura,
      ricaviMese,
      ricaviMesePrecedente,
      ricaviDeltaPerc,
      speseMese,
      margineOperativo,
      ospitiConteggio,
      fatture,
      fattureNonPagate,
      totaleNonPagato,
      alertPT,
      alertReinvio,
      occupatePerc,
      deltaOspitiMese,
      deltaPostiLiberiMese,
      postiLiberiInfo,
      diarioOggi,
      occupazioneData,
      occupazioneChartData,
      deblisteringInfo,
      debTotale,
      debAttivi,
      debPerc,
      debRicavoMensile,
      debPrezzo,
      attivitaRecenti,
      ultimiOspiti,
      scadenzeProssime,
      preIngressiWidget,
      preIngressoAvvisi,
      azioniRapide,
    ]
  );

  const preIngressoAvvisiBanner =
    preIngressoAvvisi.length > 0 ? (
      <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <p className="font-semibold flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600" />
          Ingresso previsto oggi ma pratica incompleta
        </p>
        <ul className="mt-2 space-y-1 text-amber-800">
          {preIngressoAvvisi.map((a) => (
            <li key={a.fascicolo_id}>
              {a.nome_completo}: {a.messaggio}
            </li>
          ))}
        </ul>
      </div>
    ) : null;

  const resolvedControlloCards = useMemo(() => {
    const list = Array.isArray(prefControllo) ? prefControllo : [];
    return list.filter((c) => resolveControlloCardId(c.id, fatturazioneAccess));
  }, [prefControllo, fatturazioneAccess]);

  const handleControlloDragEnd = (event) => {
    if (!isCustomizeMode || !setDraftPrefs) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setDraftPrefs((prev) => {
      const list = [...(prev.controllo || [])];
      const oldIdx = list.findIndex((c) => c.id === active.id);
      const newIdx = list.findIndex((c) => c.id === over.id);
      if (oldIdx === -1 || newIdx === -1) return prev;
      return { ...prev, controllo: arrayMove(list, oldIdx, newIdx) };
    });
  };

  const toggleControlloVisible = (id) => {
    if (!setDraftPrefs) return;
    setDraftPrefs((prev) => ({
      ...prev,
      controllo: (prev.controllo || []).map((c) =>
        c.id === id ? { ...c, visible: !c.visible } : c
      ),
    }));
  };

  const changeControlloSize = (id, newSize) => {
    if (!setDraftPrefs) return;
    const meta = CONTROLLO_CARDS_BY_ID[id];
    if (!meta?.supportedSizes?.includes(newSize)) return;
    setDraftPrefs((prev) => ({
      ...prev,
      controllo: (prev.controllo || []).map((c) =>
        c.id === id ? { ...c, size: newSize } : c
      ),
    }));
  };

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  if (loading) {
    return (
      <div className="space-y-4 md:space-y-5 animate-pulse">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="h-[100px] bg-slate-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (aggregateControlloView && hasMultiStruttura) {
    return (
      <DashboardAggregatePanel token={token} dashboardFatturazione={fatturazioneAccess} />
    );
  }

  const sortItems = resolvedControlloCards.map((c) => c.id);
  const showGrid =
    resolvedControlloCards.some((c) => (isCustomizeMode ? true : c.visible)) ||
    isCustomizeMode;

  return (
    <DndContext
      sensors={controlloSensors}
      collisionDetection={closestCenter}
      onDragEnd={handleControlloDragEnd}
    >
      {preIngressoAvvisiBanner}
      {!showGrid ? (
        <p className="text-sm text-slate-400 text-center py-12">
          Nessun blocco visibile. Attiva Personalizza per mostrarne alcuni.
        </p>
      ) : (
        <SortableContext items={sortItems} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 md:gap-4 auto-rows-auto items-stretch">
            {resolvedControlloCards.map((card) => {
              const meta = CONTROLLO_CARDS_BY_ID[card.id];
              const clamped =
                meta && meta.supportedSizes.includes(card.size)
                  ? card.size
                  : meta?.defaultSize || card.size;
              const cardMerged = clamped !== card.size ? { ...card, size: clamped } : card;
              const span = getControlloColSpanClass(cardMerged.size);
              return (
                <SortableControlloCard
                  key={cardMerged.id}
                  card={cardMerged}
                  cardData={controlloCardData}
                  isCustomizeMode={isCustomizeMode}
                  colSpanClass={`col-span-1 ${span}`}
                  onToggleVisible={() => toggleControlloVisible(cardMerged.id)}
                  onChangeSize={(id, size) => changeControlloSize(id, size)}
                />
              );
            })}
          </div>
        </SortableContext>
      )}
    </DndContext>
  );
}
